import { useMemo } from "react";

import type { ClosingTimeRequest } from "@luna-restaurant/school-api";

import { useCollection } from "@/hooks/useCollection";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getBerlinTodayDateOnlyOrEarlier } from "@/utils/closingTimes/berlinDateOnly";

type UseClosingTimeRequestsOptions = {
  forSchoolClass?: boolean;
  schoolClassIri?: string;
  effectiveAtTo?: string;
  itemsPerPage?: number;
  fetchAllPages?: boolean;
  enabled?: boolean;
  orderStartDate?: "asc" | "desc";
};

export const useClosingTimeRequests = (
  options?: UseClosingTimeRequestsOptions,
) => {
  const { currentUser } = useCurrentUser();
  const berlinToday = getBerlinTodayDateOnlyOrEarlier();

  const query = useMemo(() => {
    const params: Record<string, string> = {
      "effectiveAt[from]": berlinToday,
      "order[startDate]": options?.orderStartDate ?? "asc",
    };

    if (options?.effectiveAtTo) {
      params["effectiveAt[to]"] = options.effectiveAtTo;
    }

    if (options?.schoolClassIri) {
      params.schoolClass = options.schoolClassIri;
    } else if (typeof options?.forSchoolClass === "boolean") {
      params["exists[schoolClass]"] = String(options.forSchoolClass);
    }

    if (typeof options?.itemsPerPage === "number") {
      params.itemsPerPage = String(options.itemsPerPage);
    }

    return params;
  }, [
    berlinToday,
    options?.effectiveAtTo,
    options?.forSchoolClass,
    options?.itemsPerPage,
    options?.orderStartDate,
    options?.schoolClassIri,
  ]);

  return useCollection<ClosingTimeRequest>({
    queryKey: ["closing-time-requests", currentUser?.school],
    path: currentUser?.school
      ? `${currentUser.school}/closing-time-requests`
      : "",
    query,
    enabled: !!currentUser?.school && (options?.enabled ?? true),
    fetchAllPages: options?.fetchAllPages,
  });
};
